import { Button } from '../ui/button';
import { CanGuard } from '../../client/permissions';

interface DirtyActionBarProps {
  /** Whether the form has unsaved changes. Disables both buttons when false. */
  dirty: boolean;
  /** Whether a save is in flight. */
  saving?: boolean;
  /**
   * Blocks Save alone, leaving Discard usable. For a form whose current
   * edits are invalid: the admin must still be able to throw them away, and
   * folding validity into `dirty` would disable both buttons at once.
   */
  saveDisabled?: boolean;
  /** Discard button click handler. */
  onDiscard: () => void;
  /** Save button click handler. */
  onSave: () => void;
  /**
   * Optional permission slug; when provided, the bar is wrapped in
   * `<CanGuard>` so only authorized viewers see it.
   */
  permission?: string;
  /**
   * Team the `permission` is evaluated within, for a form that edits one
   * team. Matches the server, which checks these routes with `canInTeam`.
   */
  permissionTeam?: string;
  /** Label for the save button (default: `'Save changes'`). */
  saveLabel?: string;
  /** Label for the discard button (default: `'Discard changes'`). */
  discardLabel?: string;
  /** Label shown on the save button while a save is in flight. */
  savingLabel?: string;
}

/**
 * Discard / save footer used by detail editor pages.
 *
 * Centralizes the right-aligned flex container, button spacing, copy
 * (`'Saving…'`, `'Save changes'`), and the optional permission gate so
 * every detail page has the same shape.
 */
export function DirtyActionBar({
  dirty,
  saving = false,
  onDiscard,
  onSave,
  permission,
  permissionTeam,
  saveDisabled = false,
  saveLabel = 'Save changes',
  discardLabel = 'Discard changes',
  savingLabel = 'Saving…',
}: DirtyActionBarProps) {
  const disabled = !dirty || saving;
  const content = (
    <div className="flex items-center justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onDiscard} disabled={disabled}>
        {discardLabel}
      </Button>
      <Button type="button" onClick={onSave} disabled={disabled || saveDisabled}>
        {saving ? savingLabel : saveLabel}
      </Button>
    </div>
  );

  if (permission) {
    return (
      <CanGuard permission={permission} team={permissionTeam}>
        {content}
      </CanGuard>
    );
  }
  return content;
}
